import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Modal } from '@chikara/ui';
import { PageContent } from '@/shared/components/PageContent';
import { useCardDetail } from '@/features/card/hooks/useCard/useCardDetail';
import { CardDetailInfo } from '@/features/card/components/CardDetailPage/CardDetailInfo';
import { CardDetailActions } from '@/features/card/components/CardDetailPage/CardDetailActions';
import { CardDetailFinancial } from '@/features/card/components/CardDetailPage/CardDetailFinancial';
import { CardDetailActivity } from '@/features/card/components/CardDetailPage/CardDetailActivity';
import { ChangeNipModal } from '@/features/card/components/ChangeNipModal/ChangeNipModal';
import { EditAliasModal } from '@/features/card/components/CardDetailPage/EditAliasModal';
import { CreateTagModal } from '@/features/card/components/CardDetailPage/CreateTagModal';
import { ManageTagsModal } from '@/features/card/components/CardDetailPage/ManageTagsModal';
import { EditTagModal } from '@/features/card/components/CardDetailPage/EditTagModal';
import { ManageLimitModal } from '@/features/card/components/ManageLimitModal/ManageLimitModal';
import { isBasic } from '@/shared/config/permissions';
import { useAuth } from '@/features/auth/hooks/useAuth';
import styles from './CardDetailPage.module.css';
import { t } from '@/i18n';
import { formatCardAliasForDisplay } from '@/features/card/utils/cardDisplayLabels';


export default function CardDetailPage() {
  const { userData } = useAuth();
  const basic = isBasic(userData.role);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const row = location.state;

  const navigate = useNavigate();
  const { card, accountData, activityData, state, controls, handlers, pagination, onGetAccount } = useCardDetail(id, row);

  if (!card) return null;

  const breadcrumbItems = [
    { label: t('dashboard.card.detail.breadcrumb'), onClick: () => navigate('/card') },
    { label: formatCardAliasForDisplay(card.cardAlias) },
  ];

  return (
    <PageContent
      breadcrumb={breadcrumbItems}
      title={formatCardAliasForDisplay(card.cardAlias)}
      subtitle={`•••• ${card.maskedPan}`}
      onBack={() => navigate(-1)}
    >
      <div className={styles.container}>
        <div className={styles.infoSection}>
          <CardDetailInfo card={card} />

          <CardDetailActions
            card={card}
            accountData={accountData}
            state={state}
            controls={controls}
            handlers={handlers}
          />

          <CardDetailFinancial
            card={card}
            onManageLimit={handlers.handleManageLimit}
          />
          
          {(card.type === 'physical' || (card.type as string) === t('dashboard.card.type.physical')) &&
            (card.status === 'enabled' || card.status === 'disabled') && (
              <div className={styles.actionsSection}>
                <Button
                  variant="outline"
                  size="md"
                  onClick={handlers.handleReportTheft}
                  className={styles.actionButton}
                >
                  {t('dashboard.card.detail.actions.report')}
                </Button>
                {!basic && accountData?.view?.can_cancel && (
                  <Button
                    variant="tertiary"
                    size="md"
                    onClick={handlers.handleCancelCard}
                    className={styles.actionButton}
                  >
                    {t('dashboard.card.detail.actions.cancel')}
                  </Button>
                )}
              </div>
            )}
        </div>

        <CardDetailActivity data={activityData} pagination={pagination}/>
      </div>

      <ChangeNipModal
        isOpen={state.isChangeNipModalOpen}
        onClose={handlers.handleCloseChangeNipModal}
        cardUuid={row.card_uuid}
        accountUuid={card.account_uuid}
      />

      <EditAliasModal
        isOpen={state.isEditAliasModalOpen}
        value={state.aliasValue}
        onClose={handlers.handleCloseEditAliasModal}
        onChange={controls.setAliasValue}
        onSave={handlers.handleSaveAlias}
      />

      <CreateTagModal
        isOpen={state.isCreateTagModalOpen}
        onClose={() => controls.setIsCreateTagModalOpen(false)}
        onSave={handlers.handleCreateTag}
      />

      <ManageTagsModal
        isOpen={state.isManageTagsModalOpen}
        onClose={() => controls.setIsManageTagsModalOpen(false)}
        tags={state.tags}
        onEditTag={handlers.handleOpenEditTag}
        onDeleteTag={handlers.handleDeleteTag}
      />

      <EditTagModal
        isOpen={state.isEditTagModalOpen}
        onClose={() => controls.setIsEditTagModalOpen(false)}
        tagName={state.editingTag || ''}
        onSave={handlers.handleUpdateTag}
      />

      <ManageLimitModal
        isOpen={state.isManageLimitModalOpen}
        card={card}
        onClose={handlers.handleCloseManageLimitModal}
        onSuccess={onGetAccount}
      />

      <Modal
        isOpen={state.isCancelModalOpen}
        onClose={handlers.handleCloseCancelModal}
        title={t('dashboard.card.detail.actions.cancelConfirm.title')}
        description={t('dashboard.card.detail.actions.cancelConfirm.description')}
        actionLabel={t('dashboard.card.detail.actions.cancelConfirm.action')}
        onAction={handlers.handleConfirmCancel}
        secondaryActionLabel={t('dashboard.card.detail.actions.cancelConfirm.secondaryAction')}
        onSecondaryAction={handlers.handleCloseCancelModal}
        secondaryActionVariant="link"
      />
    </PageContent>
  );
}

export { CardDetailPage };
